import classNames from 'clsx';
import type { ComponentType } from 'react';
import React from 'react';
import { FormattedDate } from 'react-intl';
import { connect } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router';
import { compose } from 'redux';

import Button from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { ConfigurableChestProps, MpcChest } from '@swf/core/types/ChestInventory';
import { ChestLockedType } from '@swf/core/types/Notification';

import { TXTCLI_Available, TXTCLI_Expires, TXTWCL_Unlock } from './ChestTile.messages.json';
import styles from './ChestTile.scss';
import Hourglass from './assets/hourglass.svg';
import Padlock from './assets/padlock.svg';
import ConfigurableChest from './components/ConfigurableChest/ConfigurableChest';

export interface OwnProps {
  chestOnly?: boolean;
}

type StateProps = ReturnType<typeof mapState>;

export type Props = OwnProps & MpcChest & StateProps & RouteComponentProps & ConfigurableChestProps;

const getTileClass = ({ isAvailable }: Partial<Props>) =>
  classNames(styles.tile, { [styles.inactive]: !isAvailable });

const timeOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
};

export const getChestClass = ({ tierId, chestType, skinType, customSkin }: Partial<Props>) => {
  const type: string =
    chestType === 'SR'
      ? 'sr'
      : chestType === 'SRCVL2'
      ? 'srcvl2'
      : skinType === 'SPECIAL'
      ? 'special'
      : 'mpc';
  const customChest: string = customSkin ? customSkin : '';
  return classNames(
    styles.chest,
    styles[`tier${tierId}` as keyof typeof styles],
    styles[type as keyof typeof styles],
    styles[customChest as keyof typeof styles]
  );
};

class ChestTile extends React.PureComponent<Props> {
  onOpenChestClick = (chestId: number) =>
    this.props.history.replace('/account/open-chest', { chest: chestId });

  openIceChallenges = () => this.props.history.replace(`/account/ice/challenges`);

  renderIceLockedChest() {
    const { chestText, isAvailable, lockType, chestType, tierId, customSkin, logoName, assets } =
      this.props;

    return (
      <div className={getTileClass(this.props)}>
        {!isAvailable && lockType === ChestLockedType.IceLocked && (
          <div className={classNames(styles.icon, styles.iconLocked)}>
            <Padlock />
          </div>
        )}
        <span className={styles.chestName}>{chestText}</span>
        <ConfigurableChest
          assets={assets}
          chestType={chestType}
          customSkin={customSkin}
          logoName={logoName}
          tierId={tierId}
        />
        <Button
          variant="secondary"
          size="small"
          className={styles.unlockButton}
          onClick={this.openIceChallenges}
          translation={TXTWCL_Unlock}
        />
      </div>
    );
  }

  renderChest() {
    const {
      expiresOn,
      availableOn,
      chestId,
      chestText,
      isAvailable,
      lockType,
      logoName,
      chestType,
      tierId,
      customSkin,
      assets,
      hideExpiryTime
    } = this.props;

    return (
      <div
        className={getTileClass(this.props)}
        onClick={() => isAvailable && this.onOpenChestClick(chestId as number)}
      >
        {!isAvailable && lockType === ChestLockedType.TimeLocked && (
          <div className={styles.icon}>
            <Hourglass />
          </div>
        )}
        <span className={styles.chestName}>{chestText}</span>
        <ConfigurableChest
          assets={assets}
          chestType={chestType}
          customSkin={customSkin}
          logoName={logoName}
          tierId={tierId}
        />

        <small className={styles.chestAvailable}>
          {isAvailable ? (
            !hideExpiryTime && <Translation {...TXTCLI_Expires} />
          ) : (
            <Translation {...TXTCLI_Available} />
          )}
          <span>
            {isAvailable ? (
              !hideExpiryTime && <FormattedDate {...timeOptions} value={expiresOn as string} />
            ) : (
              <FormattedDate {...timeOptions} value={availableOn as string} />
            )}
          </span>
        </small>
      </div>
    );
  }

  render() {
    const { isAvailable, lockType } = this.props;

    return !isAvailable && lockType === ChestLockedType.IceLocked
      ? this.renderIceLockedChest()
      : this.renderChest();
  }
}

const mapState = (rootState: RootState) => ({ locale: select.locale.get(rootState) });

export default compose(
  withRouter,
  // @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363
  connect<StateProps>(mapState)
)(ChestTile) as ComponentType<OwnProps>;
